import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getUserCookiesConsent,
  setUserCookiesConsent,
  type UserCookiesConsent,
} from '~/utils/local-storage-utils/local-storage-utils';

export interface CookieConsentState {
  acceptedCookies: boolean;
  showCookieBanner: boolean;
  consentDate: string | null;
}

export const loadInitialState = (): CookieConsentState => {
  // New consent key takes priority
  const stored = getUserCookiesConsent();
  if (stored) {
    return {
      acceptedCookies: stored.acceptedCookies,
      showCookieBanner: stored.showCookieBanner,
      consentDate: stored.consentDate,
    };
  }

  // Migrate from legacy acceptedCookies key (pre-consent system)
  if (localStorage.getItem('acceptedCookies') === 'yes') {
    const migrated: CookieConsentState = {
      acceptedCookies: true,
      showCookieBanner: false,
      consentDate: new Date().toISOString(),
    };
    setUserCookiesConsent(migrated);
    localStorage.removeItem('acceptedCookies');
    return migrated;
  }

  return {
    acceptedCookies: false,
    showCookieBanner: true,
    consentDate: null,
  };
};

const persistConsent = (state: CookieConsentState) => {
  const consent: UserCookiesConsent = {
    acceptedCookies: state.acceptedCookies,
    showCookieBanner: state.showCookieBanner,
    consentDate: state.consentDate,
  };
  setUserCookiesConsent(consent);
};

const cookieConsentSlice = createSlice({
  name: 'cookieConsent',
  initialState: loadInitialState(),
  reducers: {
    acceptAll: {
      reducer: (state, action: PayloadAction<string>) => {
        state.acceptedCookies = true;
        state.showCookieBanner = false;
        state.consentDate = action.payload;
        persistConsent(state);
      },
      prepare: () => ({ payload: new Date().toISOString() }),
    },
    rejectNonEssential: {
      reducer: (state, action: PayloadAction<string>) => {
        state.acceptedCookies = false;
        state.showCookieBanner = false;
        state.consentDate = action.payload;
        persistConsent(state);
      },
      prepare: () => ({ payload: new Date().toISOString() }),
    },
    hideBanner: (state) => {
      state.showCookieBanner = false;
      persistConsent(state);
    },
    resetConsent: (state) => {
      state.acceptedCookies = false;
      state.showCookieBanner = true;
      state.consentDate = null;
      persistConsent(state);
    },
    setConsentState: (_state, action: PayloadAction<CookieConsentState>) => {
      persistConsent(action.payload);
      return action.payload;
    },
  },
});

export const { acceptAll, rejectNonEssential, hideBanner, resetConsent, setConsentState } =
  cookieConsentSlice.actions;

export default cookieConsentSlice.reducer;
